import * as THREE from 'three/webgpu'
import {
  isFeatureFace,
  isFeatureLine,
  isFeaturePoint,
} from '../../mesh/feature'
import type { FeatureResultMesh } from '../../mesh/feature'
import {
  disposeImportedModelTopologyHost,
  suspendImportedModelTopologyHost,
} from '../topology/imported-model-topology-runtime'
import { resolveImportedTopologyHost } from './ImportedModelTopologyPickResolver'

/** 默认最多保留的空闲 pick host 数量。 */
const DEFAULT_MAX_IDLE_PICK_HOSTS = 64
/** 默认空闲 pick host 几何与 BVH 估算预算：64 MiB。 */
const DEFAULT_MAX_IDLE_PICK_HOST_BYTES = 64 * 1024 * 1024
/** BVH 与几何树节点按原始属性字节的两倍做保守估算。 */
const PICK_HOST_BVH_ESTIMATED_BYTE_MULTIPLIER = 2

/**
 * 导入拓扑临时代理保留控制器。
 *
 * 设计说明：
 * 1. imported-model 的点线面代理只在二阶段命中后按需挂载到 pick host；
 * 2. 因此控制器负责统一回收离开 hover/selected 状态的临时代理，避免宿主无限增长；
 * 3. 如果不在统一状态机里回收这些临时代理，用户每扫过一次面或顶点，宿主下都会残留更多 Object3D，
 *    交互次数一多就会把点击和缩放拖慢；
 * 4. 控制器只关心“当前状态还需要哪些代理”，具体释放动作仍委托给 `FeatureResultMesh`，
 *    保持导入选择层与结果体资源生命周期职责分离。
 */
export class ImportedModelTopologyProxyRetentionController {
  /** 上一次同步后仍被控制的宿主集合。 */
  private retainedHosts = new Map<string, FeatureResultMesh>()
  /** 已无临时代理、但可在短期再次 Hover 时复用的 LRU 宿主。 */
  private readonly idleHosts = new Map<string, ImportedModelTopologyIdleHostEntry>()
  /** 空闲宿主数量上限。 */
  private readonly maxIdleHosts: number
  /** 空闲宿主估算字节上限。 */
  private readonly maxIdleBytes: number
  /** 当前空闲宿主估算字节数。 */
  private idleBytes = 0
  /** 累计空闲宿主复用命中次数。 */
  private cacheHits = 0
  /** 累计预算淘汰次数。 */
  private evictions = 0

  /**
   * @param options 有界空闲缓存配置；默认同时受 64 个宿主和 64 MiB 约束。
   */
  public constructor(options: ImportedModelTopologyProxyRetentionOptions = {}) {
    this.maxIdleHosts = Math.max(0, Math.floor(options.maxIdleHosts ?? DEFAULT_MAX_IDLE_PICK_HOSTS))
    this.maxIdleBytes = Math.max(0, Math.floor(options.maxIdleBytes ?? DEFAULT_MAX_IDLE_PICK_HOST_BYTES))
  }

  /**
   * 根据当前 hover / selected 状态同步导入临时代理。
   *
   * @param input 当前选择状态快照。
   */
  public sync(input: {
    hoveredPick: THREE.Intersection<THREE.Object3D> | null
    selectedPicks: THREE.Intersection<THREE.Object3D>[]
    retainedObjects?: readonly THREE.Object3D[]
    /** 额外需要保留的拓扑代理对象（如测量选中集）。 */
    additionalRetainedObjects?: readonly THREE.Object3D[]
  }): void {
    const nextStateByHost = new Map<string, RetainedImportedTopologyProxyState>()

    this.collectRetainedProxyState(nextStateByHost, input.hoveredPick?.object ?? null)
    input.selectedPicks.forEach((pick) => {
      this.collectRetainedProxyState(nextStateByHost, pick.object)
    })
    input.retainedObjects?.forEach((object) => {
      this.collectRetainedProxyState(nextStateByHost, object)
    })
    input.additionalRetainedObjects?.forEach((object) => {
      this.collectRetainedProxyState(nextStateByHost, object)
    })

    const affectedHosts = new Map<string, FeatureResultMesh>()
    this.retainedHosts.forEach((host, hostId) => {
      affectedHosts.set(hostId, host)
    })
    nextStateByHost.forEach((state, hostId) => {
      affectedHosts.set(hostId, state.host)
      this.activateIdleHost(state.host)
    })

    affectedHosts.forEach((host, hostId) => {
      const retainedState = nextStateByHost.get(hostId)
      host.retainTransientTopologyProxyObjects(retainedState?.retainedObjects ?? [])
      if (!retainedState && host.children.length === 0) {
        this.cacheIdleHost(host)
      }
    })

    this.retainedHosts = new Map(
      Array.from(nextStateByHost.entries()).map(([hostId, state]) => [hostId, state.host]),
    )
  }

  /**
   * 清空当前所有导入临时代理保留状态。
   */
  public clear(): void {
    const hosts = new Set<FeatureResultMesh>(this.retainedHosts.values())
    this.idleHosts.forEach((entry) => hosts.add(entry.host))
    hosts.forEach((host) => {
      host.retainTransientTopologyProxyObjects([])
      disposeImportedModelTopologyHost(host)
    })
    this.retainedHosts.clear()
    this.idleHosts.clear()
    this.idleBytes = 0
  }

  /** @returns 只在显式性能检查时创建的空闲缓存统计。 */
  public getStats(): ImportedModelTopologyProxyRetentionStats {
    return {
      activeHosts: this.retainedHosts.size,
      cachedIdleHosts: this.idleHosts.size,
      cachedIdleBytes: this.idleBytes,
      cacheHits: this.cacheHits,
      evictions: this.evictions,
    }
  }

  /**
   * 将刚离开交互状态的空宿主加入 LRU，并立即执行数量/字节双预算淘汰。
   *
   * @param host 已清空临时代理的 pick host。
   */
  private cacheIdleHost(host: FeatureResultMesh): void {
    const previousEntry = this.idleHosts.get(host.uuid)
    if (previousEntry) {
      this.idleBytes -= previousEntry.estimatedBytes
      this.idleHosts.delete(host.uuid)
    }
    const estimatedBytes = this.estimateHostBytes(host)
    if (
      this.maxIdleHosts === 0
      || this.maxIdleBytes === 0
      || estimatedBytes > this.maxIdleBytes
    ) {
      disposeImportedModelTopologyHost(host)
      this.evictions += 1
      return
    }

    /**
     * 空闲宿主不参与场景遍历、渲染或普通候选；必须先由 Repository 解除活动 Unit 投影，
     * 再脱离场景。找不到权威 Repository 时不能保留可能携带旧端口的宿主。
     */
    if (!suspendImportedModelTopologyHost(host)) {
      disposeImportedModelTopologyHost(host)
      return
    }

    this.idleHosts.set(host.uuid, { host, estimatedBytes })
    this.idleBytes += estimatedBytes
    while (this.idleHosts.size > this.maxIdleHosts || this.idleBytes > this.maxIdleBytes) {
      const oldestEntry = this.idleHosts.entries().next().value
      if (!oldestEntry) break
      const [hostId, entry] = oldestEntry
      this.idleHosts.delete(hostId)
      this.idleBytes -= entry.estimatedBytes
      disposeImportedModelTopologyHost(entry.host)
      this.evictions += 1
    }
  }

  /**
   * 把再次命中的空闲宿主移回活动集合，并刷新其 LRU 命中统计。
   *
   * @param host 本次交互重新引用的宿主。
   */
  private activateIdleHost(host: FeatureResultMesh): void {
    const entry = this.idleHosts.get(host.uuid)
    if (!entry || entry.host !== host) return
    this.idleHosts.delete(host.uuid)
    this.idleBytes -= entry.estimatedBytes
    this.cacheHits += 1
  }

  /**
   * 估算宿主几何、BVH 与 Provider 面几何缓存的占用。
   *
   * @param host 待缓存宿主。
   * @returns 用于预算比较的保守字节数，至少为 1。
   */
  private estimateHostBytes(host: FeatureResultMesh): number {
    const arrays = new Set<ArrayBufferView>()
    Object.values(host.geometry.attributes).forEach((attribute) => {
      const array = Reflect.get(attribute, 'array')
        ?? Reflect.get(Reflect.get(attribute, 'data') ?? {}, 'array')
      if (ArrayBuffer.isView(array)) arrays.add(array)
    })
    const indexArray = Reflect.get(host.geometry.getIndex() ?? {}, 'array')
    if (ArrayBuffer.isView(indexArray)) arrays.add(indexArray)
    let geometryBytes = 0
    arrays.forEach((array) => {
      geometryBytes += array.byteLength
    })
    const faceGeometryCacheBytes = host.getFeatureFaceGeometryCacheStats()?.cachedBytes ?? 0
    return Math.max(
      1,
      geometryBytes * PICK_HOST_BVH_ESTIMATED_BYTE_MULTIPLIER + faceGeometryCacheBytes,
    )
  }

  /**
   * 把当前命中的导入拓扑对象写入宿主保留状态。
   *
   * @param stateByHost 按宿主聚合的保留状态。
   * @param object 当前命中对象。
   */
  private collectRetainedProxyState(
    stateByHost: Map<string, RetainedImportedTopologyProxyState>,
    object: THREE.Object3D | null,
  ): void {
    if (!object) {
      return
    }

    const host = resolveImportedTopologyHost(object)
    if (!host) {
      return
    }

    const hostId = host.uuid
    const currentState = stateByHost.get(hostId) ?? {
      host,
      retainedObjects: [],
    }

    if (isFeatureLine(object) || isFeatureFace(object) || isFeaturePoint(object)) {
      const duplicated = currentState.retainedObjects.some((retainedObject) => retainedObject.uuid === object.uuid)
      if (!duplicated) {
        currentState.retainedObjects.push(object)
      }
    }

    stateByHost.set(hostId, currentState)
  }
}
